// Shared helpers for the unified-search surfaces (NavbarSearch dropdown,
// MobileSearchOverlay, SearchResultsPage). Both the flat keyboard-nav list
// and the rendered section blocks must agree on ordering, and every surface
// must agree on how an event `kind` is labeled — so this logic lives once.

// --- Shared relevance scoring -------------------------------------------
// One scoring model for BOTH the dropdown's section ordering and the results
// page's top-result pick, so the two never disagree about what ranks first.

export const foldText = (s) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Text match quality: exact 100 / prefix 70 / substring 40 / weak 10.
export const scoreMatch = (name, q) => {
    if (!name || !q) return 0;
    const n = foldText(name);
    const s = foldText(q);
    if (n === s) return 100;
    if (n.startsWith(s)) return 70;
    if (n.includes(s)) return 40;
    return 10;
};

// Does this artist own a REAL mix corpus? `hasMixes` is a boolean and a
// single misattributed mix flips it: the metal band "Boiler Room" (1 mix
// performed) claimed the 138-mix Boiler Room event as its "own series" and
// demoted it below itself. So owning means having performed at least
// OWNING_MIN_MIXES — the same "real corpus" bar events must clear for their
// scale bonus. Servers that predate `mixesPerformed` fall back to the flag.
export const OWNING_MIN_MIXES = 5;
export const ownsCorpus = (a) => (
    a.mixesPerformed != null ? a.mixesPerformed >= OWNING_MIN_MIXES : a.hasMixes !== false
);

// A producer who never performs mixes is still a real destination when DJs
// play their tracks (the artist page's "played by others" side). Missing on
// older servers → false.
export const SUPPORTED_MIN_DJS = 5;
export const isSupported = (a) => a.supportingDjs != null && a.supportingDjs >= SUPPORTED_MIN_DJS;

// A real artist entity owns a corpus OR is supported. Everything below keys
// on this — the own-series demotion (an event named after a REAL artist is
// their series; "Strobe" the party must not bury Strobe the producer, 190
// DJs play them), the artist-derived credit for their tracks/mixes, and the
// top-result fallback. A phantom (neither: the 1-mix, 1-DJ metal band
// "Boiler Room") gets none of it.
export const isRealArtist = (a) => ownsCorpus(a) || isSupported(a);

// Artist type prior: +15 for a real entity, -5 for a phantom. The penalty is
// what lets exact-match mixes/tracks outrank the phantom and file it after
// them; against nothing stronger than a prefix match the phantom still
// wins, so obscure-but-real names keep their artist page as the top result.
export const artistPrior = (a) => (isRealArtist(a) ? 15 : -5);

// An event whose name IS (or starts with) a mix-owning artist's name is that
// artist's own series ("Keinemusik Radio Show") — the artist page is the
// useful destination, so such events get no scale bonus. Returns a checker
// bound to the current artist results.
export const buildOwnSeriesChecker = (artists = []) => {
    const owning = artists
        .filter(isRealArtist)
        .map((a) => foldText(a.name))
        .filter(Boolean);
    return (evName) => {
        const n = foldText(evName);
        return owning.some((a) => n === a || n.startsWith(`${a} `));
    };
};

// Entity scores. Type priors mirror the results page's pickTopResult:
// artists +15 for a real entity / -5 for a phantom (see artistPrior — a
// corpus-less, unsupported "artist" is usually an event entity or a stray), events +20 when they have a real corpus (≥5 mixes) and are
// NOT an artist's own series, hot tracks +5.
export const scoreArtist = (a, q) => scoreMatch(a.name, q) + artistPrior(a);
export const scoreEvent = (ev, q, isOwnSeries) => {
    const base = (ev.name || '').split(',')[0].trim();
    const textScore = Math.max(scoreMatch(ev.name, q), base !== ev.name ? scoreMatch(base, q) : 0);
    // An artist's OWN series isn't just denied the scale bonus — it's
    // demoted below the artist's tracks/mixes: when you type an artist's
    // name, their tracks are the destination, and the series stays reachable
    // via the artist page. Festivals/venues keep the corpus bonus.
    if (isOwnSeries(ev.name)) return Math.max(0, textScore - 20);
    return textScore + (ev.mixCount >= 5 ? 20 : 5);
};
// artistBest: the best score among MIX-OWNING matched artists (0 if none).
// Tracks/mixes returned for an artist query often carry the artist only as
// an association (label mates, collective members) with no query text in
// their own artist/title strings — pure text scoring would file them under
// "barely related" (10) even though they're exactly what the user wants.
// So they inherit artist-derived credit: tracks land just under the artist,
// mixes just under the tracks — giving the common artist query the
// Artists → Tracks → Mixes order. A literal text match still wins where it's
// stronger (a query that names a track ranks Tracks first on its own).
export const scoreTrack = (t, q, artistBest = 0) => {
    const combined = t.artist ? `${t.artist} ${t.title}` : t.title;
    return Math.max(scoreMatch(combined, q) + (t.mixRefcount > 5 ? 5 : 0), artistBest - 20);
};
export const scoreMix = (m, q, artistBest = 0, isOwnSeries = () => false) => {
    const title = m.title || '';
    const combined = m.djName ? `${m.djName} ${title}` : title;
    // The mix's own identity is its TITLE. The combined "djName title" string
    // only counts when the query reaches INTO the title — a query that's just
    // (a prefix of) the DJ name is an artist signal, and "Keinemusik - Exit
    // Festival" must not outrank the artist's tracks on the query "keinemu".
    const reachesTitle = foldText(q).length > foldText(m.djName || '').length;
    let text = Math.max(scoreMatch(title, q), reachesTitle ? scoreMatch(combined, q) : 0);
    // Same own-series demotion as events: an episode titled with the matched
    // artist's series name ("Keinemusik Radio Show") is a strong text match
    // for a partially-typed artist name, but the artist's tracks are the
    // better destination. Once the query outgrows the artist name, the artist
    // no longer matches, isOwnSeries goes false, and the series wins again.
    if (isOwnSeries(title)) text = Math.max(0, text - 20);
    return Math.max(text, artistBest - 30);
};
// The credit source: only REAL artists count — a phantom "artist" row
// (Tomorrowland) must not drag random tracks up the list.
export const bestOwningArtistScore = (artists = [], q) =>
    artists.reduce((m, a) => (isRealArtist(a) ? Math.max(m, scoreArtist(a, q)) : m), 0);

// Dropdown section order: each section is ranked by its BEST item's score
// against the query, descending — nothing is hardcoded. "tomorrowland" →
// the event (exact match + 330-mix corpus) outranks the mix-less artist row;
// "keinemu" → the artist Keinemusik (prefix + owns mixes) outranks their own
// "Keinemusik Radio Show" series; a query that exactly names a hot track
// puts Tracks first. Ties break by the fixed order below.
// Labels score on plain text match (no priors yet): an exact label name
// ranks its section first, a weak prefix files it after the catalog.
export const scoreLabel = (l, q) => scoreMatch(l.name, q) + (l.trackCount > 20 ? 5 : 0);

const SECTION_TIE_ORDER = ['artists', 'events', 'tracks', 'mixes', 'labels'];
export const orderSections = (groups, query) => {
    const q = (query || '').trim();
    const isOwnSeries = buildOwnSeriesChecker(groups.artists || []);
    const artistBest = bestOwningArtistScore(groups.artists || [], q);
    const best = (list, score) => (list || []).reduce((m, item) => Math.max(m, score(item)), -1);
    const scores = {
        artists: best(groups.artists, (a) => scoreArtist(a, q)),
        events: best(groups.events, (ev) => scoreEvent(ev, q, isOwnSeries)),
        tracks: best(groups.tracks, (t) => scoreTrack(t, q, artistBest)),
        mixes: best(groups.mixes, (m) => scoreMix(m, q, artistBest, isOwnSeries)),
        labels: best(groups.labels, (l) => scoreLabel(l, q)),
    };
    // Labels join the order only when the server returned some — older
    // servers send none, and callers index their section lists by this key.
    const keys = SECTION_TIE_ORDER.filter((k) => k !== 'labels' || (groups.labels || []).length > 0);
    return keys.sort((a, b) =>
        (scores[b] - scores[a]) || (SECTION_TIE_ORDER.indexOf(a) - SECTION_TIE_ORDER.indexOf(b)));
};

// Dev-only introspection for QA: per-section and per-item scores.
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    window.__searchScores = (groups, query) => {
        const q = (query || '').trim();
        const isOwnSeries = buildOwnSeriesChecker(groups.artists || []);
        const artistBest = bestOwningArtistScore(groups.artists || [], q);
        return {
            artistBest,
            order: orderSections(groups, q),
            artists: (groups.artists || []).map((a) => [a.name, scoreArtist(a, q)]),
            events: (groups.events || []).map((ev) => [ev.name, scoreEvent(ev, q, isOwnSeries)]),
            tracks: (groups.tracks || []).map((t) => [`${t.artist} - ${t.title}`, scoreTrack(t, q, artistBest)]),
            mixes: (groups.mixes || []).map((m) => [`${m.djName} - ${m.title}`, scoreMix(m, q, artistBest, isOwnSeries)]),
            labels: (groups.labels || []).map((l) => [l.name, scoreLabel(l, q)]),
        };
    };
}

// Row label for an event entity: 'venue' groups come from mixes.location,
// 'series' are radio shows / podcasts (name-pattern classified in the
// mix_events_search MV), everything else is a plain event/festival.
// `type` = a source page's exact label ("Club", "Open Air / Festival", "Radio
// Station" …) when the server sent one; else the coarse kind.
const KIND_LABELS = { venue: 'Venue', series: 'Series', festival: 'Festival', radio: 'Radio station', podcast: 'Podcast' };
export const eventKindLabel = (kind, type = null) => type || KIND_LABELS[kind] || 'Event';

// The three page families the headings are built from: venues, shows
// (series / radio / podcast), events (events / festivals).
const kindFamily = (kind) => (kind === 'venue' ? 'venue' : (kind === 'series' || kind === 'radio' || kind === 'podcast') ? 'series' : 'event');

// Section header for a group of event entities — composed from the kinds
// actually present, so a group holding a radio show never sits under a
// heading that calls it an event. "shows" is the collective for series
// ("Events & series" reads awkwardly; "Series" alone stays for uniform
// groups whose rows already carry that label).
export const eventSectionHeader = (events = []) => {
    const kinds = new Set(events.map((e) => kindFamily(normalizeEventKind(e.kind))));
    if (kinds.size <= 1) {
        if (kinds.has('venue')) return 'Venues';
        if (kinds.has('series')) return 'Series';
        return 'Events';
    }
    if (kinds.size === 3) return 'Events, venues & shows';
    if (!kinds.has('series')) return 'Events & venues';
    return kinds.has('event') ? 'Events & shows' : 'Venues & shows';
};

// Natural-language heading for the mixes list a clicked event opens.
export const eventMixesHeading = (kind, name) => {
    const fam = kindFamily(kind);
    if (fam === 'venue') return `Mixes at ${name}`;
    if (fam === 'series') return `Episodes of ${name}`;
    return `Mixes from ${name}`;
};

// Normalize a server kind for pass-through (older servers only send
// event/venue; unknown values collapse to 'event').
const EVENT_KINDS = ['event', 'venue', 'series', 'festival', 'radio', 'podcast'];
export const normalizeEventKind = (kind) => (EVENT_KINDS.includes(kind) ? kind : 'event');
